import React, { useState, useEffect } from 'react';
import { 
  Leaf, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle, Trees
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';

interface EsgEcoAuditorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля екологічного та ESG комплаєнсу...",
  "ESG-аудитор оцінює рівень екологічного впливу та розраховує еко-податок...",
  "Аналітик митного регулювання ЄС розраховує вбудовані викиди CO2 та платежі CBAM...",
  "Консультант із сталого розвитку складає план декарбонізації...",
  "Формування підсумкового ESG & CBAM звіту..."
];

const LOADING_STEPS_EN = [
  "Initializing environmental & ESG compliance AI agents...",
  "ESG auditor evaluates emissions impact and national eco-tax...",
  "EU customs analyst computes embedded CO2 emissions and CBAM levies...",
  "Sustainability consultant drafts decarbonization roadmap...",
  "Generating final ESG & CBAM audit report..."
];

export default function EsgEcoAuditor({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: EsgEcoAuditorProps) {
  const isUk = language === 'uk';
  const [auditType, setAuditType] = useState(isUk ? 'Експорт до ЄС (Розрахунок CBAM та CO2)' : 'EU Export (CBAM & CO2 Calculation)');
  const [enterpriseImpactText, setEnterpriseImpactText] = useState(
    useEnterpriseContext ? (isUk ? 'Виробничі потужності та обсяги споживання енергії будуть завантажені з бази знань.' : 'Production capacities & energy consumption will be loaded from knowledge base.') : ''
  );
  const [complianceGoalsText, setComplianceGoalsText] = useState('');
  
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  useEffect(() => {
    if (report && typeof report === 'string' && report.trim().length > 0 && !report.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(report, 'ESG & Екологічний Аудит CBAM', 'esg-eco-auditor');
    }
  }, [report]);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRun = async () => {
    if (!enterpriseImpactText.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Екологічний аудит, розрахунок CBAM та ESG-комплаєнс",
          agentId: "esg-eco-auditor",
          inputs: {
            audit_type: auditType,
            enterprise_impact_text: enterpriseImpactText,
            compliance_goals_text: complianceGoalsText || (isUk ? "Стандартний ESG та CBAM комплаєнс" : "Standard ESG & CBAM compliance")
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка екологічного аналізу.' : 'Failed to run ESG audit.'));
      }

      const data = await response.json();
      
      if (data.jobId) {
        const pollJob = async () => {
          const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
          if (!statusRes.ok) throw new Error(isUk ? 'Помилка перевірки статусу.' : 'Failed to check status.');
          const statusData = await statusRes.json();
          
          if (statusData.status === 'completed') {
            setReport(statusData.report);
            setIsAuditing(false);
          } else if (statusData.status === 'failed') {
            throw new Error(statusData.error || (isUk ? 'Аналіз завершився помилкою.' : 'Analysis failed.'));
          } else {
            setTimeout(pollJob, 3000);
          }
        };
        await pollJob();
      } else if (data.report) {
        setReport(data.report);
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = "ESG_CBAM_Report_" + auditType.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-teal-600" />
              <span>{isUk ? "← До Наукового Центру" : "← Back to Science Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Leaf className="w-6 h-6 text-teal-600" />
              <span>{isUk ? "ESG & Екологічний Аудитор (CBAM)" : "ESG & Environmental Auditor (CBAM)"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Оцінка екологічного впливу (ОВД), розрахунок CBAM для експорту в ЄС, вуглецевий след та еко-податок" : "Environmental impact assessment (EIA), EU CBAM carbon border tax calculations, and ESG standards compliance"}
            </p>
          </div>
        </div>
        <div className="bg-teal-500/10 border border-teal-500/30 px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-teal-600 animate-pulse" />
          <span className="text-[10px] text-teal-600 font-bold uppercase tracking-wider">Multi-Agent AI</span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-teal-900">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-teal-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "Екологічний профіль підприємства" : "Company Environmental Profile")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-teal-500/20 rounded text-teal-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setAuditType(isUk ? "Експорт до ЄС (Розрахунок CBAM та CO2)" : "EU Export (CBAM & CO2 Calculation)");
              setEnterpriseImpactText(`Виробництво сталевого прокату та арматури (обсяг експорту до Німеччини - 5 000 тонн на рік).
Споживання природного газу: 1 200 000 куб.м/рік. Споживання електроенергії: 4 500 МВт·год/рік.
Необхідно розрахувати вбудовані викиди CO2 (Scope 1 та Scope 2) для звітності CBAM та сформувати декларацію для митниці ЄС.`);
              setComplianceGoalsText("Скласти звіт CBAM відповідно до регламенту ЄС 2023/956 та розрахувати прогнозоване мито з 2026 року.");
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (ESG & CBAM аудит експорту сталі до ЄС)" : "⚡ Try 1-click Demo Sample (CBAM & ESG Audit)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Напрямок аудиту / Регулювання" : "Audit Category / Regulation"}
                </label>
                <select
                  value={auditType}
                  onChange={(e) => setAuditType(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-teal-500 focus:outline-none text-sm transition-colors text-slate-850"
                >
                  <option value={isUk ? "Експорт до ЄС (Розрахунок CBAM та CO2)" : "EU Export (CBAM & CO2 Calculation)"}>{isUk ? "Експорт до ЄС (Розрахунок вбудованих викидів та мита CBAM)" : "EU Export (CBAM Embedded Emissions & Levies)"}</option>
                  <option value={isUk ? "Український еко-податок та викиди" : "Ukraine Eco-Tax & Emissions"}>{isUk ? "Розрахунок екологічного податку України (викиди, скиди, відходи)" : "Ukraine Eco-Tax (Emissions & Waste)"}</option>
                  <option value={isUk ? "Оцінка впливу на довкілля (ОВД)" : "Environmental Impact Assessment (EIA)"}>{isUk ? "Оцінка впливу на довкілля (ОВД для будівництва/виробництва)" : "Environmental Impact Assessment (EIA)"}</option>
                  <option value={isUk ? "ESG-сертифікація та підготовка до інвестицій" : "ESG Certification & Investor Readiness"}>{isUk ? "ESG-рейтинг та декарбонізація для залучення інвестицій" : "ESG Rating & Decarbonization for Investors"}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Характеристики виробництва / Викиди / Споживання енергії" : "Production metrics / Emissions / Energy usage"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Опишіть типи продукції (метал, цемент, добрива, електроенергія), обсяги споживання газу/електроенергії, джерела викидів CO2 чи відходів..." 
                    : "Describe products (steel, cement, fertilizers, electricity), energy/gas consumption, CO2 emissions or waste sources..."}
                  value={enterpriseImpactText}
                  onChange={(e) => setEnterpriseImpactText(e.target.value)}
                  rows={8}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Цілі комплаєнсу / Експортні ринки (необов'язково)" : "Compliance Goals / Export Markets (optional)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Вкажіть країни ЄС для експорту, вимоги партнерів чи бажані терміни декарбонізації..." 
                    : "Specify EU target countries, buyer requirements, or decarbonization targets..."}
                  value={complianceGoalsText}
                  onChange={(e) => setComplianceGoalsText(e.target.value)}
                  rows={4}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!enterpriseImpactText.trim()}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Leaf className="w-4 h-4" />
              <span>{isUk ? "Провести ESG & CBAM еко-аудит" : "Run ESG & CBAM Eco-Audit"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-teal-600" />
                <span>{isUk ? "Як це працює?" : "How it works?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-teal-100 text-teal-700 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "ESG-аудитор перевіряє національний екологічний комплаєнс та ОВД." : "ESG auditor checks national environmental compliance and EIA."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-teal-100 text-teal-700 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Митний аналітик розраховує вбудовані викиди та додаткові мита за регламентом CBAM ЄС." : "EU customs analyst calculates embedded carbon duties under CBAM rules."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-teal-100 text-teal-700 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "ШІ розробляє покрокову стратегію декарбонізації та експортного комплаєнсу." : "AI crafts step-by-step decarbonization & EU export compliance roadmap."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-teal-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-teal-600 rounded-full animate-spin" />
            <Leaf className="w-6 h-6 text-teal-600" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Екологічний та CBAM аудит аналітичними модулями..." : "Running ESG & CBAM audit with AI..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-teal-50 p-1.5 rounded border border-teal-100">
                <FileText className="w-4 h-4 text-teal-600" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Звіт ESG & CBAM аудиту" : "ESG & CBAM Audit Report"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setEnterpriseImpactText('');
                setComplianceGoalsText('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Створити новий еко-аудит" : "New Eco-Audit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
